import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, BarChart3 } from 'lucide-react';
import { cn, formatDisplayDateOnly, formatScore, scoreColor } from '@/lib/utils';
import { BetScoreBreakdown } from './BetScoreBreakdown';
import type { MyBetsQuery } from '@/gql/graphql';

type BetItem = NonNullable<NonNullable<MyBetsQuery['myBets']>['items']>[number];

interface ScoredBetCardProps {
    bet: BetItem;
}

export function ScoredBetCard({ bet }: ScoredBetCardProps) {
    const { t } = useTranslation('bets');

    const race = bet.race;
    const score = bet.score ?? 0;
    const rankings = useMemo(() => bet.rankings ?? [], [bet.rankings]);
    const bonuses = bet.bonuses ?? [];

    // Group rankings by gender
    const genders = useMemo(() => [...new Set(rankings.map(r => r.gender).filter(Boolean))] as string[], [rankings]);

    return (
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
                <div className="flex-1 min-w-0">
                    <Link
                        to={`/races/${race?.id}`}
                        className="font-bold text-slate-900 hover:text-emerald-700 transition-colors truncate block"
                    >
                        {race?.name}
                    </Link>
                </div>
                <div className="text-right flex-shrink-0">
                    <div className="text-xs text-slate-400 font-medium">
                        {t('list.score')}
                    </div>
                    <div className={cn("text-xl font-black", scoreColor(score))}>
                        {formatScore(score)}
                    </div>
                </div>
            </div>

            {/* Gender sections */}
            <div className="space-y-4">
                {genders.map((gender) => {
                    const genderRankings = rankings.filter(r => r.gender === gender);
                    const genderBonuses = bonuses.filter(b => b.gender === gender || b.gender == null);
                    const genderLabel = gender === 'MALE' ? t('gender.MALE') : t('gender.FEMALE');

                    return (
                        <div key={gender}>
                            <div className="flex items-center justify-between mb-1">
                                <span className={cn(
                                    "inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide",
                                    gender === 'MALE' ? "bg-blue-50 text-blue-600" : "bg-pink-50 text-pink-600"
                                )}>
                                    {genderLabel}
                                </span>
                            </div>
                            <BetScoreBreakdown
                                rankings={genderRankings}
                                bonuses={gender === genders[genders.length - 1] ? genderBonuses : bonuses.filter(b => b.gender === gender)}
                            />
                        </div>
                    );
                })}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 text-xs text-slate-400 pt-3 mt-4 border-t border-slate-100">
                {race?.bettingDeadline && (
                    <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDisplayDateOnly(race.bettingDeadline)}
                    </span>
                )}
                <Link
                    to={`/races/${race?.id}/leaderboard`}
                    className="flex items-center gap-1 ml-auto text-emerald-600 font-bold hover:text-emerald-700 transition-colors"
                >
                    <BarChart3 size={12} />
                    {t('list.viewLeaderboard')}
                </Link>
            </div>
        </div>
    );
}
